import { API } from "@/lib/api";
import {
  FbaBatch,
  FbaBatchItem,
  FbaBox,
  FbaShipment,
  FbaShipmentPatch,
  PlacementOption,
} from "@/contexts/ShipmentContext";
import { Supplier } from "@/contexts/InventoryContext";
import {
  BxError,
  AddItemToInboundBatchBody,
  AddItemToInboundBatchResponse,
  CreateInboundBatchBody,
  CreateInboundBatchResponse,
  FBA_API_VERSION,
  FbaShipmentStatus,
  ListInboundBatchesResponse,
  PalletBody,
  PalletResponse,
  PollingFbaListingsResponse,
  ProductCondition,
  SubmitListingsResponse,
  TransportType,
  ShipmentCreateBoxBody,
  ShipmentUpdateBoxBody,
  PollingFbmListingsResponse,
  GenerateTransportOptionsPollingResponse,
  PrepCategoriesBody,
  PrepCategoriesPollingResponse,
  PrepCategory,
  PrepCategoriesResponse,
  PrepInstructionV2,
  CancelBatchResponse,
  PatchInboundBatchItemBody,
  PatchInboundBatchItemResponse,
  PlacementOptionConfirmSPDResponse,
  UpdateInboundBatchResponse,
  UpdateInboundBatchBody,
  GetPrepDetailsResponse,
  SubmitBoxesResponse,
  SubmitItemsResponse,
  GeneratePlacementOptionsPollingResponse,
  GeneratePlacementOptionsResponse,
  PlacementOptionConfirmLTLResponse,
  InboundBatch,
  FBABoxDTO,
  ListShipmentsSort,
  FBABox,
  ShipmentUpdateBoxDimensionsBody,
} from "@boxem/core-api-schema";
import { FbaListing } from "@/contexts/FbaListingContext";
import { FbmListing } from "@/contexts/FbmListingContext";
import { logRocketError } from "@/utils/logUtils";
import { User } from "@/contexts/UserContext";

export type ReceivedBatchForIncompleteTable = {
  batch: FbaBatch;
  placementOptionsStatusesFromIncompleteBatchesDTO?: PlacementOption["status"][];
};

export async function getBatchesForIncompleteTable(): Promise<ReceivedBatchForIncompleteTable[]> {
  let apiBatches = [];

  // first request, get the total count
  const params = new URLSearchParams();
  params.append("pageSize", `0`);
  params.append("page",     `0`);
  const countObj = await API.get<ListInboundBatchesResponse>(`${FBA_API_VERSION}/fba/batchesForIncompleteTable`, params);
  console.log(countObj);
  const count = countObj.total;
  if (count > 0) {
    // this may need some tweaking to find the ideal value, and will change as the db entities change. 
    // We saw problems in Miles' account when he hit ~400 batches, so it certainly show be below that.
    // @NOTE(michaelp): Regarding the above comment, now that the query is cheaper, the expense may be less
    // page size and more in boilerplate run by SQL and in our infrastructure.
    // Our maximum test case right now is Miles' with 260 incomplete batches.
    // A page size of 100 and 260 take 400ms regardless.
    // I'm going to try 1000 and we can see if we run into problems.
    const pageSize = 1000;
    // make X requests in parallel to get the remainder,
    // where X is the number of pages, given by:
    const pages = Math.ceil(count / pageSize);
    const requests: Promise<ListInboundBatchesResponse>[] = [];
    for (let i = 0; i < pages; i++) {
      const params = new URLSearchParams();
      params.append("pageSize", `${pageSize}`);
      params.append("page",     `${i}`);
      requests.push(API.get<ListInboundBatchesResponse>(`${FBA_API_VERSION}/fba/batchesForIncompleteTable`, params))
    }
    await Promise.all(requests).then(results => {
      results.forEach(result => apiBatches.push(...result.batches));
    });
  }

  if (apiBatches.length !== count) {
    logRocketError(`When loading batches, we got a total of ${count} but only ended up loading ${apiBatches.length} in total...`)
  }

  const result = apiBatches.map(apiBatch => {
    let r: ReceivedBatchForIncompleteTable = {
      batch: new FbaBatch(apiBatch),
      placementOptionsStatusesFromIncompleteBatchesDTO: apiBatch.placementOptionsStatuses,
    };
    return r;
  });
  return result;
}

export async function getBatch(batchId: number): Promise<FbaBatch> {
  return API.get<InboundBatch>(`${FBA_API_VERSION}/fba/batches/${batchId}`).then((batch) => new FbaBatch(batch));
}

export function removeItemFromBatch(batchId: number, batchItemId: number) {
  return API.delete(`${FBA_API_VERSION}/fba/batches/${batchId}/items/${batchItemId}`);
}

export const createSupplier = (name: string) => {
  return API.post<Supplier>(`/fba/suppliers`, { name });
};

export function getSuppliers() {
  return API.get<Supplier[]>(`/fba/suppliers`);
}

export function getSupplierById(id: number) {
  return API.get<Supplier>(`/fba/suppliers/${id}`);
}

export type ListingRestrictionInput = {
  asin: string;
  condition: ProductCondition;
};
export const getListingRestrictions = async (input: ListingRestrictionInput[]) => {
  return await API.post<Array<BxError[]>>("/fba/listing-restrictions", input, null);
};

//This endpoint just fetches listings for the given user where the SKU of the listing is in the input array of skus.
export const pollFbaListings = async (input: string[]) => {
  return await API.post<PollingFbaListingsResponse>("/fba/poll-fba-listings", input, null);
};

export const submitFbaListings = async (input: FbaListing[]) => {
  return await API.post<SubmitListingsResponse>("/fba/listings", input, null);
};

export const pollFbmListings = async (input: string[]) => {
  return await API.post<PollingFbmListingsResponse>("/fbm/poll-fbm-listings", input, null);
};

export const submitFbmListings = async (input: FbmListing[]) => {
  return await API.post<SubmitListingsResponse>("/fbm/listings", input, null);
};

export function getPrepInstructionsForASINs(asins: string[]) { // @DEPRECATED
  // const testList: string[] = [
  //   // "B077C7HBRF", "B08QBJB2K4", "B07FKFPXXQ", "B08Y7MHFJB", "0803636652", "0803627882", "0323529496", "0803644175", "0803659423", "0803657048",
  //   // "B007HQI9P8", "B0058Z33FG", "B008F0JGUW", "B00KSD5NBA", "B01LQU71LI", "B00J2H514C", "B006AWKUHA", "B010RS6N6Y", "B009D4B1SO", "B07TKX2VM2",
  //   // "B01GQMZ6OK", "B098QL7BRV", "B08RRFPMN8", "B07KD8NJDX", "B07VWXG5PM", "B0BRP3T7CP", "B0059CW34K", "B078NKWQ1Q", "B00K6P04NW", "B008F060TW",

  //   // this row of ASINS is all consumables:
  //   "B076H2J9TZ",
  //   "B07179XBP9",
  //   "B0761YSFL9",
  //   "B07222R3Y5",
  //   "B0C9D5576K",
  //   "B0BL7316GD",
  //   "B0CRGN4N55",
  //   "B07WYXPPC2",
  //   "B002U0KUTE",
  //   "B002PK0ROM",
  // ];
  return API.get<any>(`/fba/prepInstructions`, new URLSearchParams({ asins: asins.join(",") }));
}

export interface TransportReview {
  status: FbaShipmentStatus;

  // only set if the status field is 'TRANSPORT_ESTIMATED':
  id?: number;
  estimateAmount?: string;
  estimateCurrency?: string;
  confirmDeadline?: string;
  voidDeadline?: string;
  boxes?: { id: number; trackingId: string; carrierName: string; inboundPackageStatus: string }[];
  transportSubmissionError?: string;
}


export function cancelShipment(shipmentId: number) { // @TODO(nick) remove
  return API.post<void>(`/fba/shipments/${shipmentId}/cancel`);
}

export function getShipmentV2(shipmentId: string) {
  return API.get<FbaShipment>(`${FBA_API_VERSION}/fba/shipments/${shipmentId}`);
};

export async function getShippingLabelsV2(shipmentId: number): Promise<Blob> {
  const params = API.handleSeller();

  return fetch(`${API.prefix}${FBA_API_VERSION}/fba/shipments/${shipmentId}/shipping-label${params ? "?" + params.toString() : ""}`, { 
    method: "GET",
    headers: {
      Authorization: await API.getAccessToken(),
    },
    credentials: "include"
  }).then(response => response.blob());
}

export function getShippedShipments(page: number, pageSize: number, term?: string, sort?: ListShipmentsSort, ascending: boolean = false) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (!!term) params.append('searchTerm', term);
  if (!!sort) params.append('sort', sort);
  params.append('ascending', ascending.toString());

  return API.get(`${FBA_API_VERSION}/fba/shipments/shipped`, params)
    .then((res: any) => {
      return {
        shipments: res.shipments.map(shipment => new FbaShipment(shipment)),
        count: res.count,
        page: res.page,
        limit: res.limit,
      }
    });
}

export function getClosedShipments(page: number, pageSize: number, term?: string, sort?: ListShipmentsSort, ascending: boolean = false) {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());
  if (!!term) params.append('searchTerm', term);
  if (!!sort) params.append('sort', sort);
  params.append('ascending', ascending.toString());
  return API.get(`${FBA_API_VERSION}/fba/shipments/closed`, params)
    .then((res: any) => {
      return {
        shipments: res.shipments.map(shipment => new FbaShipment(shipment)),
        count: res.count,
        page: res.page,
        limit: res.limit,
      }
    });
}

export function addPalletsV2(shipmentId: number, pallets: PalletBody[]): Promise<PalletResponse[]> {
  return API.post(`${FBA_API_VERSION}/fba/shipments/${shipmentId}/pallets`, pallets);
}


export function updatePalletV2(palletId: number, body: PalletBody): Promise<PalletResponse> {
  return API.patch(`${FBA_API_VERSION}/fba/pallets/${palletId}`, body);
}

export function deletePaletteByIdV2(palletId: number): Promise<void> {
  return API.delete(`${FBA_API_VERSION}/fba/pallets/${palletId}`);
}

// if we get back an empty object, that means the BOL is not yet ready.
export function getBillOfLadingV2(shipmentId: number) {
  return API.get<{ url?: string }>(`${FBA_API_VERSION}/fba/shipments/${shipmentId}/billOfLading`);
}


export function getSyncStatus() {
  return API.get<Partial<User>>(`/sync`);
}

export function markShipmentAsShipped(shipmentId: number) {
  return API.post<{ status: FbaShipmentStatus }>(`/fba/shipments/${shipmentId}/shipped`);
}

export function patchShipmentV2(shipmentId: number, patch: FbaShipmentPatch): Promise<FbaShipment> {
  return API.patch<FbaShipment>(`${FBA_API_VERSION}/fba/shipments/${shipmentId}`, patch);
}

export function createBatchV2(body: CreateInboundBatchBody): Promise<FbaBatch> {
  return API.post<CreateInboundBatchResponse>(`${FBA_API_VERSION}/fba/batches`, body).then(batch => new FbaBatch(batch));
}

export function updateBatchV2(batchId: number, patch: UpdateInboundBatchBody): Promise<UpdateInboundBatchResponse> {
  return API.patch<UpdateInboundBatchResponse>(`${FBA_API_VERSION}/fba/batches/${batchId}`, patch);
}

export function addItemToBatchV2(batchId: number, body: AddItemToInboundBatchBody): Promise<FbaBatchItem> {
  return API.post<AddItemToInboundBatchResponse>(`${FBA_API_VERSION}/fba/batches/${batchId}/items`, body).then(item => new FbaBatchItem(item));
}

export function updateItemInBatchV2(batchId: number, body: PatchInboundBatchItemBody) {
  return API.patch<PatchInboundBatchItemResponse>(`${FBA_API_VERSION}/fba/batches/${batchId}/items`, body);
}

export function createBoxesV2(shipmentType: TransportType, body: ShipmentCreateBoxBody, packingOptionId?: string, shipmentId?: number) {
  if (shipmentType === TransportType.LTL) {
    return API.post<FbaBox[]>(`${FBA_API_VERSION}/fba/shipments/${shipmentId}/boxes`, body);
  }
  return API.post<FbaBox[]>(`${FBA_API_VERSION}/fba/packing/${packingOptionId}/boxes`, body);
}

export const handleCreateBox = (
  currentBatch: FbaBatch,
  setCurrentBatch: (batch: FbaBatch) => void, // @TODO change to just normal setCurrentBatch @DEPRECATED
  boxes = [ { items: [] }], // default value creates a single empty box
  inboundPackingGroupId?: string,
  shipmentId?: number,
): Promise<FbaBox[]> => {
  return createBoxesV2(currentBatch.shipmentType, {
    boxes: boxes,
    inboundPackingGroupId: inboundPackingGroupId,
  }, currentBatch.chosenPackingOption.packingOptionId, shipmentId)
    .then(newBoxes => {
      currentBatch.boxes = [
        ...currentBatch.boxes, ...newBoxes.map(box => new FbaBox(box as FBABoxDTO))
      ];
      setCurrentBatch({...currentBatch});
      return newBoxes;
    })
};

export function updateBoxV2(boxId: number, body: ShipmentUpdateBoxBody) {
  return API.patch<FbaBox[]>(`${FBA_API_VERSION}/fba/packing/${boxId}`, body);
}

export function updateBoxDimensionsV2(boxId: number, body: ShipmentUpdateBoxDimensionsBody) {
  return API.patch<FbaBox[]>(`${FBA_API_VERSION}/fba/setBoxDimensions/${boxId}`, body);
}

export function deleteBoxV2(boxId: number) {
  return API.delete<void>(`${FBA_API_VERSION}/fba/packing/${boxId}`);
}

export function submitItems(batchId: number) {
  return API.pollingRequestMultiple<SubmitItemsResponse>(
    "POST",
    `${FBA_API_VERSION}/fba/batches/${batchId}/submit-items`,
    null,
    null,
    5000,
  );
}

// LTL only:
export function generatePlacementOptionsLTL(batchId: number): AsyncGenerator<GeneratePlacementOptionsPollingResponse, never, void> {
  return API.pollingRequestMultiple<GeneratePlacementOptionsResponse>(
    "POST",
    `${FBA_API_VERSION}/fba/batches/${batchId}/placements`,
    null,
    null,
    2500,
  );
}

export function confirmPackingGroups(batchId: number) {
  return API.pollingRequestMultiple<SubmitBoxesResponse>(
    "POST",
    `${FBA_API_VERSION}/fba/batches/${batchId}/submit-boxes`,
    null,
    null,
    5000,
  );
}

// SPD only!
export function confirmPlacementOption(placementOptionId: string, batchId: number): AsyncGenerator<PlacementOptionConfirmSPDResponse, never, void> {
  return API.pollingRequestMultiple(
    "POST",
    `${FBA_API_VERSION}/fba/batches/${batchId}/confirm/${placementOptionId}`,
    null,
    null,
    10_000 // slow interval, because this one takes several minutes to complete
  );
}

export function confirmPlacementOptionLTL(placementOptionId: string): Promise<PlacementOptionConfirmLTLResponse> {
  return API.pollingRequest(
    "POST",
    `${FBA_API_VERSION}/fba/placements/${placementOptionId}/confirm`,
  );
}

export function getTransportationOptions(placementOptionId: string): Promise<GenerateTransportOptionsPollingResponse> {
  return API.pollingRequest("POST", `${FBA_API_VERSION}/fba/placementOptions/${placementOptionId}/transport`, null, null, 5000);
}

export function getPrepDetails(mskus: string[]): Promise<GetPrepDetailsResponse> {
  const params = new URLSearchParams();
  params.append("mskus", mskus.join(","));
  return API.get(`${FBA_API_VERSION}/fba/prepDetails`, params);
}

// we only do one SKU at a time right now. this returns a generator function which is probably confusing, @TODO explain
export function setPrepDetails(msku: string, prepCategory: PrepCategory): AsyncGenerator<PrepCategoriesPollingResponse, never, void> {
  let prepTypes: PrepInstructionV2[] = [];
  switch (prepCategory) {
    case "NONE":
      prepTypes = ["ITEM_NO_PREP"];
  }
  const body: PrepCategoriesBody = { prepCategoriesInput: [ { msku, prepCategory, prepTypes } ]};
  return API.pollingRequestMultiple<PrepCategoriesResponse>("POST", `${FBA_API_VERSION}/fba/prepDetails`, body, null, 2000, true, "operationId");
}

type ConfirmTransportationOptionsResponse = {
  keepPolling: boolean;
  errors?: any;
  data?: {
    id: number;
    boxes: FBABox[];
    bolUrl: string;
    bolS3Url: string;
    bolNumber?: string;
  }[];
};

// transportationOptionIds should be the id of the transportation option for each shipment option inside the placement option
export function confirmTransportationOption(placementOptionId: string, transportationOptionIds: string[]) {
  return API.pollingRequestMultiple(
    "POST", `${FBA_API_VERSION}/fba/transport/${placementOptionId}/confirm`, transportationOptionIds
  ) as AsyncGenerator<ConfirmTransportationOptionsResponse, never, void>;
}

export function cancelBatch(batchId: number) {
  return API.pollingRequest<CancelBatchResponse>("PATCH", `${FBA_API_VERSION}/fba/batches/${batchId}/cancel`, {}, null, 2000, true, "operationId", 30);
}

